import { z } from 'zod';

/** How long RevenueCat's own identifiers may be, before we refuse to store one. */
export const REVENUECAT_ID_MAX_LENGTH = 255;

/**
 * The event types this platform recognises.
 *
 * Listed as a constant rather than hidden in the mapper's `switch` so the
 * mapper's spec can iterate it and fail when a type is added here without a
 * mapping — an unmapped type is otherwise indistinguishable from a type
 * RevenueCat invented after we shipped, and only one of those is a defect.
 *
 * `TEST` is here because the RevenueCat dashboard's "send test webhook" button
 * sends one: an operator wiring the integration up must get a 200, or the
 * integration looks broken at the exact moment they are checking it.
 */
export const REVENUECAT_EVENT_TYPES = [
  'INITIAL_PURCHASE',
  'RENEWAL',
  'UNCANCELLATION',
  'SUBSCRIPTION_EXTENDED',
  'NON_RENEWING_PURCHASE',
  'EXPIRATION',
  'BILLING_ISSUE',
  'CANCELLATION',
  'REFUND_REVERSED',
  'PRODUCT_CHANGE',
  'SUBSCRIPTION_PAUSED',
  'TRANSFER',
  'TEMPORARY_ENTITLEMENT_GRANT',
  'PRICE_INCREASE_CONSENT_REQUIRED',
  'PRICE_INCREASE_CONSENT_APPROVED',
  'SUBSCRIBER_ALIAS',
  'EXPERIMENT_ENROLLMENT',
  'INVOICE_ISSUANCE',
  'VIRTUAL_CURRENCY_TRANSACTION',
  'PURCHASE_REDEEMED',
  'TEST',
] as const;

export type RevenueCatEventType = (typeof REVENUECAT_EVENT_TYPES)[number];

/**
 * The `cancel_reason` that means a **refund**, not an unsubscribe.
 *
 * RevenueCat sends `CANCELLATION` for both "the customer turned auto-renew off"
 * and "support refunded this purchase", and only the second takes access away
 * immediately. Naming the discriminator is what stops the two collapsing into
 * one branch — see {@link RevenueCatEventSchema}'s note on `cancel_reason`.
 */
export const REVENUECAT_REFUND_CANCEL_REASON = 'CUSTOMER_SUPPORT';

/** `environment` on a purchase the store took no money for. */
export const REVENUECAT_SANDBOX_ENVIRONMENT = 'SANDBOX';

const OptionalId = z.string().trim().min(1).max(REVENUECAT_ID_MAX_LENGTH).nullish();

/**
 * One RevenueCat webhook event, as it arrives.
 *
 * ## This schema is deliberately NOT `.strict()` — the one N7 deviation
 *
 * N7 requires request schemas to reject unknown keys, and the reason it gives is
 * catching a **client's** typo. RevenueCat is not our client: it is a vendor that
 * adds fields to this payload on its own schedule, and a strict schema would turn
 * the next one it adds into a 400 on every delivery. A rejected delivery is a
 * *lost purchase* — the member paid, the store took the money, and the platform
 * refused the only notification it will get. So this validates every field we
 * consume and silently ignores the rest, which is Zod's default strip behaviour.
 *
 * ## Almost everything is nullish, and that is the payload, not caution
 *
 * The shape genuinely varies by event type: a `TEST` event carries no product, a
 * lifetime purchase carries no expiry, and only `PRODUCT_CHANGE` carries
 * `new_product_id`. Requiring a field that one event type omits would reject that
 * type wholesale. What is required is what every event has: an id and a type.
 * `app_user_id` is not — a `TRANSFER` names its accounts in `transferred_from` /
 * `transferred_to` instead, and refusing it would have RevenueCat retry it.
 */
export const RevenueCatEventSchema = z.object({
  /** The idempotency key. `billing_events.external_event_id` is this, uniquely. */
  id: z.string().trim().min(1).max(REVENUECAT_ID_MAX_LENGTH),
  /**
   * Left as a plain string rather than the enum above: an unrecognised type must
   * reach the mapper (which records it and answers 200) instead of being refused
   * by validation, or RevenueCat retries a type we will never handle forever.
   */
  type: z.string().trim().min(1).max(80),
  app_user_id: OptionalId,
  original_app_user_id: OptionalId,
  /** The store product id — matched against a product's ios/android id. */
  product_id: OptionalId,
  /** Only on `PRODUCT_CHANGE`: what the next renewal will bill for. */
  new_product_id: OptionalId,
  /** `APP_STORE` | `PLAY_STORE` | `MAC_APP_STORE` | `AMAZON` | `STRIPE` | … */
  store: z.string().trim().min(1).max(40).nullish(),
  /** `PRODUCTION` | `SANDBOX` — a sandbox purchase (TestFlight, App Review) took no money. */
  environment: z.string().trim().min(1).max(40).nullish(),
  period_type: z.string().trim().min(1).max(40).nullish(),
  /**
   * Why a `CANCELLATION` fired. {@link REVENUECAT_REFUND_CANCEL_REASON} is the
   * one value that revokes access; every other reason leaves the member paid up
   * until `EXPIRATION` arrives.
   */
  cancel_reason: z.string().trim().min(1).max(60).nullish(),
  /** Epoch milliseconds. `null` for a purchase that never expires. */
  expiration_at_ms: z.number().int().nullish(),
  purchased_at_ms: z.number().int().nullish(),
  /** Only on `BILLING_ISSUE`: when the store's grace period ends, or `null` without one. */
  grace_period_expiration_at_ms: z.number().int().nullish(),
  event_timestamp_ms: z.number().int().nullish(),
  transaction_id: OptionalId,
  /** The stable handle on one subscription across every renewal of it. */
  original_transaction_id: OptionalId,
  entitlement_ids: z.array(z.string()).nullish(),
  /**
   * What the store took, in the storefront's currency (D9a). RevenueCat omits
   * both on some sandbox and test deliveries, and a one-off fee's ledger row
   * records them when present and `null` when not.
   */
  price: z.number().nullish(),
  currency: z.string().trim().min(1).max(3).nullish(),
});

export type RevenueCatEvent = z.infer<typeof RevenueCatEventSchema>;

/** The delivery envelope. RevenueCat wraps every event in this. */
export const RevenueCatWebhookSchema = z.object({
  api_version: z.string().nullish(),
  event: RevenueCatEventSchema,
});

export type RevenueCatWebhookPayload = z.infer<typeof RevenueCatWebhookSchema>;
